import {t} from "./i18n.js"
import {installFrameClickAway} from "./frame_click_away"

export function installSharedPlatformRuntime() {
  if (window.__seivaSharedRuntime) return
  window.__seivaSharedRuntime = true
  installFrameClickAway()
// O espelho da Documentação dentro do frame — ver o listener de clique abaixo.
const DOCS_FRAME_PATH = /^(?:\/embed)?\/(committee|aios)\/builders\/resources\/docs(?:\/|$)/

// A Committee resource/editor is a regular LiveView inside a same-origin
// iframe. Phoenix live navigation updates that iframe's history, so report
// every location change to the parent shell. The server owns the mapping from
// internal/legacy paths to canonical `/committee/...` URLs.
function installCommitteeFrameNavigationBridge() {
  if (window.self === window.parent) return

  try {
    if (["AgentResourceFrame", "AgentSkillsFrame"].includes(window.frameElement?.getAttribute("phx-hook"))) return
    if (!/^\/(committee|aios)(?:\/|$)/.test(window.parent.location.pathname)) return
    if (
      !window.parent.document.getElementById("committee-workspace-shell") &&
      !window.parent.document.getElementById("committee-room-shell")
    ) return
  } catch (_error) {
    return
  }

  const notifyParent = () => {
    window.parent.postMessage(
      {
        type: "seiva:committee:navigate",
        path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
      window.location.origin,
    )
  }

  // The parent shell owns ALL browser history for committee navigation. The
  // frame is a puppet and must never grow its own joint-history entries — a
  // single user action stacking 2-3 entries is what desyncs the Back button and
  // strands the frame on a chromeless/full-chrome state. So convert the frame's
  // own pushState into replaceState (LiveView still swaps content; it just stops
  // adding history) and report the resulting location to the parent, which
  // writes the single canonical top-window history entry.
  const originalReplaceState = window.history.replaceState.bind(window.history)

  window.history.pushState = function (...args) {
    const result = originalReplaceState(...args)
    queueMicrotask(notifyParent)
    return result
  }
  window.history.replaceState = function (...args) {
    const result = originalReplaceState(...args)
    queueMicrotask(notifyParent)
    return result
  }

  document.addEventListener("click", event => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest("a[href]")
    if (!link) return
    if (link.target === "_blank" || link.hasAttribute("download")) return

    const destination = new URL(link.href, window.location.origin)
    if (destination.origin !== window.location.origin) return
    if (
      !["/committee", "/aios"].includes(destination.pathname) &&
      !destination.pathname.startsWith("/committee/") && !destination.pathname.startsWith("/aios/")
    ) return

    // A doc é o único recurso que navega DENTRO de si: índice, categoria e
    // página são três rotas do MESMO live_session, e o sumário linka de uma
    // para a outra. Entregar esses cliques ao pai quebra a Sala — lá o pai só
    // RECONCILIA estado (a URL pública é a do Projeto, não a do recurso) e não
    // tem para onde dirigir o frame, então o clique morria no preventDefault
    // abaixo e a doc ficava presa na página principal. Deixando o frame
    // navegar sozinho, o pushState logo acima já avisa o pai: o Cockpit segue
    // escrevendo `/committee/docs/...` na URL, e sem recarregar o iframe,
    // porque ele já está no destino quando o drive chega.
    if (DOCS_FRAME_PATH.test(destination.pathname)) return

    event.preventDefault()
    event.stopImmediatePropagation()
    window.parent.postMessage(
      {
        type: "seiva:committee:navigate",
        path: `${destination.pathname}${destination.search}${destination.hash}`,
      },
      window.location.origin,
    )
  }, true)

  // Intentionally NO popstate / phx:page-loading-stop listeners and no initial
  // notify: Back/Forward belong to the parent's top window, which re-drives the
  // frame via contentWindow.location.replace on its own popstate. Notifying here
  // would echo the parent's own drives back at it and re-pollute joint history.
}

installCommitteeFrameNavigationBridge()

// The Committee Cockpit's Overview hosted as an App (`kind: "home"`): this
// document is the App viewer (`MyAppViewLive`, `committee_origin=home&
// chrome=none`) sitting between the Committee shell and the App's own iframe.
// The App cannot reach the shell (it is two frames down), so this bridge
// relays a closed set of messages both ways:
//
//   App → shell: seiva:host:ready | navigate | hitl | settings, accepted ONLY
//     from the contentWindow of the App iframe this document renders, and
//     re-posted as `seiva:committee:home` (the shell hook accepts it only from
//     its own frame and the server revalidates every field);
//   shell → App: `seiva:committee:home-message` from the parent, re-posted to
//     the App as `seiva:host:<kind>` for a whitelisted kind.
//
// It also reports `unavailable` when the viewer could not open the App, so the
// shell falls back to the default Overview instead of showing a dead frame.
function installCommitteeHomeRelay() {
  if (window.self === window.parent) return

  const params = new URLSearchParams(window.location.search)
  if (params.get("committee_origin") !== "home" || params.get("chrome") !== "none") return

  try {
    const committeeHost = /^\/(committee|aios)(?:\/|$)/.test(window.parent.location.pathname) && window.parent.document.getElementById("committee-workspace-shell")
    const aiosHost = window.parent.location.pathname === "/aios" && window.parent.document.getElementById("aios-cockpit")
    if (!committeeHost && !aiosHost) return
  } catch (_error) {
    return
  }

  const toShell = payload =>
    window.parent.postMessage({type: "seiva:committee:home", ...payload}, window.location.origin)

  const appFrame = () => document.querySelector('[data-testid="cockpit-app-frame"]')

  window.addEventListener("message", event => {
    if (event.origin !== window.location.origin) return
    const data = event.data
    if (!data || typeof data.type !== "string") return

    // Shell → App.
    if (event.source === window.parent && data.type === "seiva:committee:home-message") {
      const kind = data.message?.type
      if (!["open-editor", "theme"].includes(kind)) return
      try {
        appFrame()?.contentWindow?.postMessage(
          {...data.message, type: `seiva:host:${kind}`},
          window.location.origin,
        )
      } catch (_error) {
        // App iframe mid-load: nothing to deliver to.
      }
      return
    }

    // App → shell.
    const frame = appFrame()
    if (!frame || event.source !== frame.contentWindow) return

    switch (data.type) {
      case "seiva:host:ready":
        toShell({action: "ready"})
        break
      case "seiva:host:navigate":
        if (typeof data.path === "string") toShell({action: "navigate", path: data.path.slice(0, 2000)})
        break
      case "seiva:host:hitl":
        if (typeof data.approvalId === "string")
          toShell({action: "hitl", approvalId: data.approvalId.slice(0, 64)})
        break
      case "seiva:host:settings":
        toShell({action: "settings"})
        break
      // The App's live feed lost access: the shell resolves the Overview again.
      case "seiva:host:revoked":
        toShell({action: "unavailable"})
        break
    }
  })

  let reportedUnavailable = false
  const reportIfUnavailable = () => {
    if (reportedUnavailable) return
    if (!document.querySelector("[data-committee-home-unavailable]")) return
    reportedUnavailable = true
    toShell({action: "unavailable"})
  }

  const watch = () => {
    reportIfUnavailable()
    new MutationObserver(reportIfUnavailable).observe(document.body, {childList: true, subtree: true})
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", watch)
  else watch()
}

installCommitteeHomeRelay()

// Embed navigation bridge — embed sessions (meta `seiva-embed`, emitted by
// the embed_root layout) run inside a cross-origin partner iframe, so the
// host page cannot read the frame's location. Report every navigation
// instead: the Accountfy shell uses it to position its own chrome (the
// floating home button) per surface. Unlike the committee bridge above, the
// frame owns its history here — pushState is left untouched and popstate is
// reported too. Hosts that do not listen simply ignore the message.
function installEmbedNavigationBridge() {
  if (window.self === window.parent) return
  if (!document.querySelector("meta[name='seiva-embed']")) return

  const targetOrigin = () => {
    try {
      return new URL(document.referrer).origin
    } catch (_error) {
      return "*"
    }
  }

  const notifyParent = () => {
    window.parent.postMessage(
      {
        type: "seiva:embed:navigate",
        path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
      targetOrigin(),
    )
  }

  const originalPushState = window.history.pushState.bind(window.history)
  const originalReplaceState = window.history.replaceState.bind(window.history)

  window.history.pushState = function (...args) {
    const result = originalPushState(...args)
    queueMicrotask(notifyParent)
    return result
  }
  window.history.replaceState = function (...args) {
    const result = originalReplaceState(...args)
    queueMicrotask(notifyParent)
    return result
  }

  window.addEventListener("popstate", notifyParent)
  window.addEventListener("hashchange", notifyParent)

  // Botão nativo do rail (data-seiva-embed-home) pede ao host para voltar à
  // home do parceiro. Delegado no document porque o LiveView re-renderiza o
  // rail via morphdom — o listener sobrevive às trocas de nó.
  document.addEventListener("click", event => {
    if (!(event.target instanceof Element)) return
    if (!event.target.closest("[data-seiva-embed-home]")) return
    window.parent.postMessage({ type: "seiva:embed:go-home" }, targetOrigin())
  })

  notifyParent()
}

installEmbedNavigationBridge()

// OAuth consent must replace the Committee shell, never the resource iframe.
// Each provider's destination is deliberately restricted to its own
// same-origin initiation endpoint; the controller then performs the external
// redirect to the provider.
const OAUTH_TOP_NAVIGATE_INITIATORS = {
  "phx:google_oauth:top_navigate": "/auth/google/initiate",
}

for (const [eventName, initiatorPath] of Object.entries(OAUTH_TOP_NAVIGATE_INITIATORS)) {
  window.addEventListener(eventName, event => {
    const destination = event.detail?.to
    if (typeof destination !== "string") return

    try {
      const url = new URL(destination, window.location.origin)
      if (url.origin !== window.location.origin) return
      if (url.pathname !== initiatorPath) return

      window.top.location.assign(url.toString())
    } catch (_error) {
      // Ignore malformed destinations instead of navigating the parent window.
    }
  })
}


// Scroll a target element into view. Used when collapsing the PRD card so the
// user lands on the now-collapsed header instead of empty space below, and by
// the Statements report nav (DRE / BP / DFC), whose targets sit inside the
// section's own scroll port rather than the document.
window.addEventListener("phx:scroll_into_view", e => {
  const id = e.detail && e.detail.id
  if (!id) return
  requestAnimationFrame(() => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  })
})

// Set credential type search field value (used after creating a custom credential type)
window.addEventListener("phx:set_type_field_value", e => {
  const value = e.detail && e.detail.value
  if (!value) return
  requestAnimationFrame(() => {
    const el = document.querySelector('[data-testid="credentials-type-search"]')
    if (el) el.value = value
  })
})

// Focus on a target element (used after creating credential type to focus on Name field)
window.addEventListener("phx:focus", e => {
  const selector = e.detail && e.detail.to
  if (!selector) return
  requestAnimationFrame(() => {
    const el = document.querySelector(selector)
    if (el) el.focus()
  })
})

// A cópia vem do catálogo Gettext (via `#seiva-i18n`), não de um switch por
// `lang` — três idiomas embutidos aqui divergiam do resto da plataforma.
function confirmCopy() {
  return { title: t("Confirm action"), cancel: t("Cancel"), confirm: t("Confirm") }
}

function closePlatformConfirm(modal, restoreFocus) {
  modal.remove()
  if (restoreFocus && typeof restoreFocus.focus === "function") restoreFocus.focus()
}

function escapeHtml(value) {
  const div = document.createElement("div")
  div.textContent = value || ""
  return div.innerHTML
}

function openPlatformConfirm(message, onConfirm, restoreFocus) {
  const copy = confirmCopy()
  const modal = document.createElement("div")
  // The featured SMALL frame is the platform's current modal standard
  // (radius-sm corners, gradient hairline) — the confirm must look like a
  // sibling of every other dialog, not like the sheet it once was.
  modal.className = "seiva-modal-root flex items-end sm:items-center justify-center p-0 sm:p-4"
  // A confirmation is the one modal that stays centered; every content modal
  // opens as the side drawer (see `.seiva-modal-root` in app.css).
  modal.dataset.placement = "center"
  if (document.documentElement.classList.contains("aios-document")) modal.classList.add("aios-confirm")
  const safeMessage = escapeHtml(message)
  modal.innerHTML = `
    <div class="seiva-modal-overlay" data-confirm-cancel></div>
    <div class="seiva-modal-featured-frame seiva-modal-featured-frame--sm seiva-modal-panel--sm" role="dialog" aria-modal="true" aria-labelledby="platform-confirm-title">
      <div class="seiva-modal-featured-panel">
        <button type="button" class="seiva-modal-close absolute top-3 right-3 z-10" data-confirm-cancel aria-label="${copy.cancel}">
          <span aria-hidden="true">&times;</span>
        </button>
        <h3 id="platform-confirm-title" class="pr-8 text-sm font-semibold text-[var(--text-primary)]">${copy.title}</h3>
        <p class="mt-2 text-sm text-[var(--text-secondary)]">${safeMessage}</p>
        <div class="mt-5 flex justify-end gap-2">
          <button type="button" class="seiva-btn seiva-btn--secondary seiva-btn-size--md" data-confirm-cancel>${copy.cancel}</button>
          <button type="button" class="seiva-btn seiva-btn--danger seiva-btn-size--md" data-confirm-accept>${copy.confirm}</button>
        </div>
      </div>
    </div>
  `

  const onCancel = () => closePlatformConfirm(modal, restoreFocus)
  const onKeydown = event => {
    if (event.key === "Escape") onCancel()
  }

  modal.addEventListener("click", event => {
    if (event.target.closest("[data-confirm-cancel]")) onCancel()
    if (event.target.closest("[data-confirm-accept]")) {
      closePlatformConfirm(modal, restoreFocus)
      onConfirm()
    }
  })
  modal.addEventListener("keydown", onKeydown)
  document.body.appendChild(modal)
  modal.querySelector("[data-confirm-accept]")?.focus()
}

document.addEventListener("click", event => {
  const target = event.target.closest("[data-confirm]")
  if (!target || target.dataset.platformConfirmSkip === "true") return

  const message = target.getAttribute("data-confirm")
  if (!message) return

  event.preventDefault()
  event.stopImmediatePropagation()

  openPlatformConfirm(message, () => {
    const original = target.getAttribute("data-confirm")
    target.dataset.platformConfirmSkip = "true"
    target.removeAttribute("data-confirm")
    target.click()
    window.setTimeout(() => {
      target.setAttribute("data-confirm", original)
      delete target.dataset.platformConfirmSkip
    }, 0)
  }, target)
}, true)


}
